import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { Checkbox } from '../components/ui/checkbox'
import { type BreadcrumbProps, EditableText, H1, Icon } from '@blueprintjs/core'
import { HTMLSelect } from '../components/ui/html-select'
import { Callout } from '../components/ui/callout'
import { axiosGet, axiosPatch, axiosPost } from '../util/api'
import { capitalCase } from 'change-case'
import type { SelectBoxOption } from '../components/SelectBoxes'
import { ErrorWindow } from '../components/actions2/TriggerError'
import { TriggerEditor } from '../components/actions2/Editor'
import debounce from 'lodash.debounce'
import { PreviewTable } from '../components/actions2/PreviewTable'
import { Layout } from '../components/Layout'
import { Toasts } from '../components/toaster/Toast'
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom'
import Recipe = Models.Recipe
import { useUser } from '../hooks/useUser'
import { DateInput } from '@blueprintjs/datetime'
import { type ItemRenderer, Select } from '@blueprintjs/select'
import { Dialog } from '../components/ui/dialog'
import { format } from 'date-fns'
import { getFrequencyDescription } from '../components/recipes/util'
import { DateScheduleBox } from '../components/recipes/DateScheduleBox'
import { FrequencySelectRadios } from '../components/recipes/FrequencySelectRadios'
import FrequencyParams = Models.FrequencyParams
import Repeat = Models.Repeat
import Day = Models.Day
import Frequency = Models.Frequency
import { FrequenciesList } from '../util/contstants'
import { observer } from 'mobx-react-lite'
import { recipeStore } from '../stores/recipe_store'
import { WorkflowPanel } from '../components/recipes/workflow/WorkflowPanel'
import { toJS } from 'mobx'
import classNames from 'classnames'
import { Button } from '../components/ui/button'
import { customFieldKeyspaceForAdType, useCustomFieldCatalog } from '../features/custom-fields'
import { scheduledSegmentsApi, scheduledSegmentsApiError } from '../features/scheduled-segments/api'
import type { BigintId, ScheduleDraft } from '../features/scheduled-segments/types'
import { ScheduledRunsTable } from '../features/scheduled-segments/components/ScheduledRunsTable'
import { toastUpgradedToV2Refusal } from '../features/recipe-upgrades/enable-refusal'

const LightningIcon = ({ width = 16, height = 16, className = '' }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
    </svg>
)

export const formatPreviewSummary = (stats: { total: number | null; error: string | null }) =>
    stats.error ? 'Preview unavailable' : `${stats.total ?? 0} items`

const opaqueId = (value: unknown): BigintId | null => {
    if (typeof value !== 'string' && typeof value !== 'number') return null
    const id = String(value)
    return /^\d+$/.test(id) ? id : null
}

const isV2Recipe = (recipe: Pick<Recipe, 'version'> | null | undefined) => {
    const version = recipe?.version as unknown
    return version === 2 || version === '2'
}

export const MerchJarAPI = {
    Recipes: {
        Browse: (params: any) =>
            axiosGet('recipes', params).then((res) => {
                res.data.data = res.data.data.map((d: Recipe) => {
                    if (d?.frequency_params?.date) {
                        d.frequency_params.date = new Date(d.frequency_params.date)
                    }
                    return d
                })
                return res
            }),
        Read: (recipeId: number, version: number = 1) => {
            if (version == 1 || version == 2) {
                return axiosGet(`recipes/${recipeId}`).then((res) => {
                    if (res?.data?.frequency_params?.date) {
                        res.data.frequency_params.date = new Date(res.data.frequency_params.date)
                    }
                    return res
                })
                // } else if (version == 2) {
                // throw new Error(`TODO: Implement Read for v2`)
            } else {
                throw new Error(`Invalid version: ${version}`)
            }
        },
        Edit: (recipeId: number, recipe: Recipe, version: number = 1) => {
            if (version == 1) {
                return axiosPatch(`recipes/${recipeId}`, recipe)
            } else if (version == 2) {
                return axiosPatch(`api/v5/recipes/v2/${recipeId}`, recipe, true)
            } else {
                throw new Error(`Invalid version: ${version}`)
            }
        },
        Add: (recipe: Recipe, version: number = 1) => {
            if (version == 1) {
                return axiosPost('recipes', recipe)
            } else if (version == 2) {
                return axiosPost('api/v5/recipes/v2', recipe, true)
            } else {
                throw new Error(`Invalid version: ${version}`)
            }
        },
    },
}

export const MerchJarSegmentsAPI = {
    Segments: {
        Browse: (params: any) =>
            axiosGet('recipes', params).then((res) => {
                res.data.data = res.data.data.map((d: Recipe) => {
                    if (d?.frequency_params?.date) {
                        d.frequency_params.date = new Date(d.frequency_params.date)
                    }
                    return d
                })
                return res
            }),
        Read: (recipeId: number, version: number = 2) => {
            if (version == 1 || version == 2) {
                return axiosGet(`recipes/${recipeId}`).then((res) => {
                    if (res?.data?.frequency_params?.date) {
                        res.data.frequency_params.date = new Date(res.data.frequency_params.date)
                    }
                    return res
                })
            } else {
                throw new Error(`Invalid version: ${version}`)
            }
        },
        Edit: (recipeId: number, recipe: Recipe, version: number = 2) => {
            if (version == 1) {
                return axiosPatch(`recipes/${recipeId}`, recipe)
            } else if (version == 2) {
                return axiosPatch(`api/v5/recipes/v2/${recipeId}`, recipe, true)
            } else {
                throw new Error(`Invalid version: ${version}`)
            }
        },
        Add: (recipe: Recipe, version: number = 2) => {
            if (version == 1) {
                return axiosPost('recipes', recipe)
            } else if (version == 2) {
                return axiosPost('api/v5/recipes/v2', recipe, true)
            } else {
                throw new Error(`Invalid version: ${version}`)
            }
        },
    },
}

export const MerchJarPublicRecipesAPI = {
    Recipes: {
        Browse: (params: any) => axiosGet('admin/public-recipes', params),
        Read: (recipeId: number) => axiosGet(`admin/public-recipes/${recipeId}`),
        Edit: (recipeId: number, recipe: Recipe) =>
            axiosPatch(`admin/public-recipes/${recipeId}`, recipe),
        Add: (recipe: Recipe) => axiosPost('admin/public-recipes', recipe),
    },
}

interface RecipeDetailProps {
    asPublicRecipe?: boolean
}

export const RecipeDetail = observer(({ asPublicRecipe = false }: RecipeDetailProps) => {
    return <RecipeDetailChild asPublicRecipe={asPublicRecipe} />
})

export const RecipeDetailChild = observer(({ asPublicRecipe = false }: RecipeDetailProps) => {
    const navigate = useNavigate()
    const { recipeId } = useParams()
    const [searchParams] = useSearchParams()
    const location = useLocation()
    const { activeProfile, user } = useUser()
    const API = asPublicRecipe ? MerchJarPublicRecipesAPI : MerchJarAPI
    const previewReloadRef = useRef<(() => void) | null>(null)
    const previewBackend = searchParams.get('preview_backend') ?? searchParams.get('backend')
    const routeRecipeKey = `${location.pathname}:${recipeId}`
    // This route itself establishes the V2 scheduling context for a new
    // private segment; unlike an existing segment, there is no recipe-read
    // response from which to derive that fact.
    const isPrivateNewV2SegmentRoute =
        !asPublicRecipe && location.pathname.startsWith('/segments') && recipeId === 'new'
    const [initializedRouteRecipeKey, setInitializedRouteRecipeKey] = useState<string | null>(null)
    // The recipe-read response is the authoritative version for an existing
    // segment. Keep this separate from the editing store's duplicated version
    // field so the scheduling capability cannot be hidden by stale local state.
    const [loadedRecipeIsV2, setLoadedRecipeIsV2] = useState(false)
    // A v2 create may finish while its schedule write is still pending. Keep the
    // freshly assigned opaque ID across the replace navigation so a retry edits
    // that segment instead of issuing a second create.
    const createdSegmentIdRef = useRef<BigintId | null>(null)
    const customFieldEntityType =
        recipeStore.version === 2 && !asPublicRecipe
            ? customFieldKeyspaceForAdType(recipeStore.recipe?.ad_type)
            : null
    const {
        editorContext: customFieldEditorContext,
        error: customFieldCatalogError,
        loading: customFieldCatalogLoading,
    } = useCustomFieldCatalog(
        !asPublicRecipe && activeProfile ? String(activeProfile.profile_id) : null,
        customFieldEntityType,
    )

    useEffect(() => {
        const isCreatedSegmentRoute = recipeId !== 'new' && recipeId === createdSegmentIdRef.current
        if (isCreatedSegmentRoute) {
            recipeStore.setRecipeId(recipeId)
            setInitializedRouteRecipeKey(routeRecipeKey)
            return
        }
        // A later visit to a genuinely new/different route must not inherit an
        // ID from a failed schedule save on the previous editor instance.
        createdSegmentIdRef.current = null
        recipeStore.init()
        setLoadedRecipeIsV2(false)
        const id = recipeId == 'new' ? 'new' : recipeId
        recipeStore.setRecipeId(id)
        const isSegmentsPage = location.pathname.startsWith('/segments')
        if (isSegmentsPage && recipeId === 'new') {
            recipeStore.setVersion(2)
        }
        setInitializedRouteRecipeKey(routeRecipeKey)
    }, [routeRecipeKey])

    // Update width and height on window resize
    useEffect(() => {
        const handleResize = () => {
            const rightPanelWidth = getDefaultRightPanelWidth()
            setEditorWidth(100 - rightPanelWidth)
            const nextTotalHeight = availableEditorHeight()
            setTotalHeight(nextTotalHeight)
            setUpperSectionHeight((height) =>
                Math.min(height, Math.max(250, nextTotalHeight - 300)),
            )
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        if (!activeProfile || initializedRouteRecipeKey !== routeRecipeKey) {
            return
        }
        const loadRecipeAndSchedule = async () => {
            const profile = opaqueId(activeProfile.profile_id)
            if (isPrivateNewV2SegmentRoute && profile) {
                recipeStore.setScheduleContext(profile, null)
                await recipeStore.loadScheduleDraftTimezones(profile)
            }

            const rec = await recipeStore.load()
            if (rec) {
                validate(rec.trigger, rec)
            }

            const isPrivatePersistedV2Segment =
                !asPublicRecipe &&
                location.pathname.startsWith('/segments') &&
                recipeId !== 'new' &&
                isV2Recipe(rec)
            setLoadedRecipeIsV2(isPrivatePersistedV2Segment)
            // Prefer the raw route segment: a server response typed as a
            // number may already have rounded a bigint before reaching us.
            const segment = recipeId === 'new' ? opaqueId(rec?.id) : opaqueId(recipeId)
            if (isPrivatePersistedV2Segment && profile && segment) {
                // Time zones depend on the schedule route context. Keep this
                // serial so a stale schedule response cannot be followed by a
                // time-zone response for a newer recipe/profile.
                await recipeStore.loadSchedule(profile, segment)
                await recipeStore.loadScheduleTimezones(profile, segment)
            }
        }
        void loadRecipeAndSchedule()
    }, [activeProfile, user, initializedRouteRecipeKey, routeRecipeKey, isPrivateNewV2SegmentRoute])

    const [error, setError] = useState(null)
    const [errorValue, setErrorValue] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)

    const LS_UPPER_HEIGHT = 'mj_recipe_upper_height'
    const LS_EDITOR_WIDTH = 'mj_recipe_editor_width'
    const DEFAULT_UPPER_SECTION_HEIGHT = 380
    const MIN_EDITOR_HEIGHT = 360

    const availableEditorHeight = () => Math.max(MIN_EDITOR_HEIGHT, window.innerHeight - 100)

    // Calculate default width for Workflows/Frequency panel (300px)
    const getDefaultRightPanelWidth = () => {
        const screenWidth = window.innerWidth
        const rightPanelPercentage = (300 / screenWidth) * 100
        return Math.max(20, Math.min(50, rightPanelPercentage)) // Constrain between 20% and 50%
    }

    const [upperSectionHeight, setUpperSectionHeight] = useState(() => {
        try {
            const saved = localStorage.getItem(LS_UPPER_HEIGHT)
            return saved ? Number(saved) : DEFAULT_UPPER_SECTION_HEIGHT
        } catch {
            return DEFAULT_UPPER_SECTION_HEIGHT
        }
    })
    const [totalHeight, setTotalHeight] = useState(() => {
        // Keep the workflow panel within a short viewport so it can scroll.
        return availableEditorHeight()
    })
    const [isResizing, setIsResizing] = useState(false)
    const [isResizingWidth, setIsResizingWidth] = useState(false)
    const [showActionsPane, setShowActionsPane] = useState(true)
    const [showTriggerEditor, setShowTriggerEditor] = useState(true)
    const [errorCalloutHeight, setErrorCalloutHeight] = useState(42)
    const errorCalloutRef = useRef<HTMLDivElement>(null)

    const [editorWidth, setEditorWidth] = useState(() => {
        try {
            const saved = localStorage.getItem(LS_EDITOR_WIDTH)
            return saved ? Number(saved) : 100 - getDefaultRightPanelWidth()
        } catch {
            return 100 - getDefaultRightPanelWidth()
        }
    })
    const validTrigger = !error
    const [profileId, setProfileId] = useState<number | null>(null)

    // "On certain days" must keep ≥1 day — the legacy modal enforced this via a
    // disabled Save; the Workflow panel commits live, so gate Save here.
    const legacyScheduleValid =
        recipeStore.recipe?.frequency !== FrequenciesList.OnCertainDays ||
        (recipeStore.recipe?.frequency_params?.days?.length ?? 0) > 0
    const isPrivateV2Segment =
        !asPublicRecipe &&
        location.pathname.startsWith('/segments') &&
        (isPrivateNewV2SegmentRoute ||
            isV2Recipe(recipeStore.recipe) ||
            (recipeId !== 'new' && loadedRecipeIsV2))
    const detailScheduleProfileId = opaqueId(activeProfile?.profile_id)
    const detailScheduleRecipeId = recipeId === 'new' ? null : opaqueId(recipeId)
    const wantsClockSchedule = isPrivateV2Segment && recipeStore.scheduleDraft !== null
    // Scheduled saves wait for the schedule service's authoritative preview.
    const scheduleValid =
        legacyScheduleValid &&
        (!isPrivateV2Segment ||
            (!recipeStore.scheduleLoading &&
                (!wantsClockSchedule ||
                    (recipeStore.scheduleEditorValid &&
                        recipeStore.schedulePreviewValid &&
                        !recipeStore.schedulePreviewLoading))))

    // Track error callout height for TriggerEditor sizing
    useEffect(() => {
        if (!errorCalloutRef.current) {
            setErrorCalloutHeight(0)
            return
        }

        const resizeObserver = new ResizeObserver((entries) => {
            const height = entries[0].contentRect.height
            setErrorCalloutHeight(height)
        })

        resizeObserver.observe(errorCalloutRef.current)
        return () => resizeObserver.disconnect()
    }, [error])

    // Measure error callout height immediately on mount and when component updates
    useEffect(() => {
        if (errorCalloutRef.current) {
            const height = errorCalloutRef.current.offsetHeight
            setErrorCalloutHeight(height)
        }
    })

    useEffect(() => {
        if (activeProfile?.profile_id != profileId) {
            if (!profileId) {
                setProfileId(activeProfile?.profile_id ?? null)
            } else {
                navigate(asPublicRecipe ? '/admin/public-recipes' : '/recipes')
            }
        }
    }, [activeProfile])

    const debouncedValidate = useCallback(
        debounce((trigger, recipe) => {
            validate(trigger, recipe)
            recipeStore.setPendingRefresh(true) // Mark as dirty when trigger changes
        }, 500),
        [],
    )

    const save = async () => {
        setSaving(true)
        const isSegmentsPage = location.pathname.startsWith('/segments')
        const scheduleProfileId = opaqueId(activeProfile?.profile_id)
        const desiredEnabled = recipeStore.recipe.enabled
        const desiredPaused = recipeStore.schedulePaused
        // Keep the exact draft/revision selected at click time. A user edit
        // while an earlier prerequisite request is in flight must remain local
        // instead of being overwritten by that request's eventual PUT result.
        const desiredSchedule = recipeStore.scheduleDraft
            ? ({ ...recipeStore.scheduleDraft } as ScheduleDraft)
            : null
        const desiredScheduleRevision = recipeStore.scheduleDraftRevision
        // The replace navigation is intentionally asynchronous. Once POST has
        // assigned a segment ID, that ref is authoritative even if a retry is
        // clicked before the route params have caught up; otherwise we would
        // issue a second create for the same user action.
        const routeCreatedSegmentId = recipeId === 'new' ? createdSegmentIdRef.current : null
        const saveRecipeId = routeCreatedSegmentId ?? recipeId
        const isCreatingRecipe = saveRecipeId === 'new'
        const isScheduledMode = isPrivateV2Segment && desiredSchedule !== null
        const inactiveDuplicate =
            recipeStore.schedule?.next_run_at == null &&
            recipeStore.schedule?.last_scheduled_at == null
        const needsScheduleWrite =
            isScheduledMode &&
            (isCreatingRecipe ||
                recipeStore.schedule === null ||
                recipeStore.scheduleDirty ||
                (inactiveDuplicate && desiredEnabled))
        const enteringScheduledMode =
            isPrivateV2Segment &&
            isScheduledMode &&
            !isCreatingRecipe &&
            recipeStore.recipe.frequency === FrequenciesList.Scheduled &&
            recipeStore.schedule === null
        // A new/disabled segment must stay disabled until the schedule exists.
        // Conversely, a user who explicitly disabled or paused an existing
        // segment expects that safety state to reach the API before a schedule
        // PUT can make it runnable. Enabling/unpausing remains the final step.
        const prerequisiteEnabled = isCreatingRecipe
            ? false
            : desiredEnabled === false
              ? false
              : recipeStore.originalEnabled
        const recipeForFirstWrite = needsScheduleWrite
            ? {
                  ...toJS(recipeStore.recipe),
                  ...(isCreatingRecipe
                      ? { frequency: FrequenciesList.Manually, frequency_params: {} }
                      : {}),
                  // Keep the persisted state locked while a schedule PUT is in
                  // flight. New/previously-disabled copies remain disabled;
                  // an existing live segment is not accidentally shut off.
                  enabled: prerequisiteEnabled,
                  ...(!isCreatingRecipe && desiredPaused ? { paused: true } : {}),
              }
            : {
                  ...toJS(recipeStore.recipe),
                  ...(isScheduledMode ? { paused: desiredPaused } : {}),
              }

        try {
            let savedRecipeId: BigintId | null = isCreatingRecipe ? null : opaqueId(saveRecipeId)

            if (isCreatingRecipe) {
                const response = await API.Recipes.Add(
                    recipeForFirstWrite as Recipe,
                    recipeStore.version,
                )
                savedRecipeId = opaqueId(response?.data?.id) ?? opaqueId(response?.data?.data?.id)
                if (!savedRecipeId) {
                    throw new Error('The segment was created without a valid ID.')
                }

                createdSegmentIdRef.current = savedRecipeId
                recipeStore.setRecipe({
                    ...recipeForFirstWrite,
                    id: savedRecipeId as unknown as number,
                } as unknown as typeof recipeStore.recipe)
                // setRecipe records the disabled persisted baseline. Restore
                // the local intent so a failed PUT can be retried without
                // silently converting an enabled copy into a disabled one.
                recipeStore.setEnabled(desiredEnabled)
                recipeStore.setSchedulePaused(desiredPaused)
                recipeStore.setRecipeId(savedRecipeId)
                if (isScheduledMode && scheduleProfileId) {
                    recipeStore.setScheduleContext(scheduleProfileId, savedRecipeId)
                }
                // Replace before the first schedule request: failures stay on
                // this concrete route and retry the PUT, never POST a duplicate.
                navigate(`/segments/${savedRecipeId}`, { replace: true })
            } else if (!enteringScheduledMode) {
                await API.Recipes.Edit(
                    savedRecipeId as unknown as number,
                    recipeForFirstWrite as Recipe,
                    recipeStore.version,
                )
                if (isPrivateV2Segment) {
                    recipeStore.markRecipeStatePersisted(recipeForFirstWrite.enabled)
                }
            }

            if (needsScheduleWrite) {
                if (!scheduleProfileId || !savedRecipeId || !desiredSchedule) {
                    throw new Error('A profile and segment ID are required to save this schedule.')
                }
                const scheduleWrite = recipeStore.beginScheduleWrite(desiredScheduleRevision)
                const details = await scheduledSegmentsApi.save(
                    scheduleProfileId,
                    savedRecipeId,
                    desiredSchedule,
                )
                recipeStore.applySavedSchedule(details, scheduleWrite)
                // Controls remain interactive while the authoritative PUT is
                // in flight. Honor the latest safety intent instead of the
                // values captured when Save was first clicked.
                const finalEnabled = recipeStore.recipe.enabled
                const finalPaused = recipeStore.schedulePaused
                // The v2 route expects `paused`; do not rely on the legacy
                // paused_at field that can be present on copied recipes.
                await MerchJarAPI.Recipes.Edit(
                    savedRecipeId as unknown as number,
                    (enteringScheduledMode
                        ? { ...recipeForFirstWrite, enabled: finalEnabled, paused: finalPaused }
                        : { enabled: finalEnabled, paused: finalPaused }) as unknown as Recipe,
                    2,
                )
                recipeStore.markRecipeStatePersisted(finalEnabled)
            }

            setSaving(false)
            Toasts.success(
                isCreatingRecipe
                    ? isSegmentsPage
                        ? 'Segment created'
                        : 'Recipe created'
                    : isSegmentsPage
                      ? 'Segment saved'
                      : 'Recipe saved',
            )
            if (asPublicRecipe) {
                navigate('/admin/public-recipes')
            } else if (isSegmentsPage) {
                navigate('/segments')
            } else {
                navigate('/recipes')
            }
        } catch (err) {
            setSaving(false)
            toastUpgradedToV2Refusal(err)
            if (isPrivateV2Segment) {
                recipeStore.setScheduleError(scheduledSegmentsApiError(err))
            }
            console.log('err is', err)
        }
    }

    const validate = (trigger = null, recipe: Recipe) => {
        if (recipeStore.version == 1) {
            validate_v1(trigger, recipe)
        } else if (recipeStore.version == 2) {
            validate_v2(trigger, recipe)
        } else {
            throw new Error(`Invalid version: ${recipeStore.version}`)
        }
    }

    const validate_v1 = (trigger = null, recipe: Recipe) => {
        setErrorValue(trigger || recipe.trigger)
        axiosPost(
            'recipes/validate',
            {
                trigger: trigger || recipe.trigger,
                ad_type: recipe.ad_type,
            },
            null,
            { ignore: true },
        )
            .then(() => {
                setError(null)
                setErrorValue(null)
            })
            .catch((err) => {
                console.log('err', err.response)
                setError(err.response.data)
            })
    }

    const validate_v2 = (trigger = null, recipe: Recipe) => {
        setErrorValue(trigger || recipe.trigger)
        axiosPost(
            'api/v5/recipes/validate',
            {
                trigger: trigger || recipe.trigger,
                ad_type: recipe.ad_type,
            },
            true,
            { ignore: true },
        )
            .then((res) => {
                setError(null)
                setErrorValue(null)
                recipeStore.setVariables(res.data.variables)
            })
            .catch((err) => {
                console.log('err', err.response)
                setError(err.response.data)
            })
    }

    const recipeLoadedForRoute = recipeId === 'new' || String(recipeStore.recipe?.id) === recipeId
    if (
        initializedRouteRecipeKey !== routeRecipeKey ||
        recipeStore.loading ||
        !recipeStore.recipe ||
        !recipeLoadedForRoute
    ) {
        return null
    }

    const primaryTitle = asPublicRecipe ? (
        'Public Recipes'
    ) : (
        <div className="flex min-w-0 items-center">
            <EditableText
                value={recipeStore.recipe.name}
                onChange={(name) => recipeStore.setName(name)}
                placeholder="Recipe name..."
            />
        </div>
    )

    const centerActions = !asPublicRecipe ? (
        <div className="flex items-center gap-2">
            {/* Enable/disable lives in the Workflow panel's Enabled pill. */}
            <HTMLSelect
                className="font-normal"
                iconProps={{ icon: 'caret-down' }}
                value={recipeStore.recipe.ad_type}
                onChange={(e) => {
                    recipeStore.setAdType(parseInt(e.target.value, 10))
                }}
                options={recipeStore.availableAdTypes.map((x) => {
                    return {
                        label: `Source: ${x.label}`,
                        value: x.value,
                        disabled: x.disabled ?? false,
                    }
                })}
            />
            <Button
                intent={recipeStore.pendingRefresh ? 'warning' : 'none'}
                outlined
                // minimal={!recipeStore.pendingRefresh}
                icon={
                    <span className="inline-flex h-4 w-4 items-center justify-center">
                        <Icon
                            icon="refresh"
                            size={12}
                            className={recipeStore.previewStats.loading ? 'animate-spin' : ''}
                        />
                    </span>
                }
                onClick={(e) => {
                    e.stopPropagation()
                    previewReloadRef.current?.()
                    recipeStore.setPendingRefresh(false)
                }}
            >
                <span
                    className={classNames('ml-2', {
                        'bp4-skeleton': recipeStore.previewStats.loading,
                    })}
                >
                    {formatPreviewSummary(recipeStore.previewStats)}
                </span>
            </Button>
        </div>
    ) : null

    const rightActions = !asPublicRecipe ? (
        <div className="flex items-center gap-2">
            {recipeStore.version == 2 && (
                <Button
                    minimal
                    outlined
                    icon="clean"
                    text="AI Copilot"
                    onClick={() => window.open('https://merchjar.com/copilot', '_blank')}
                />
            )}
            <div className="inline-flex items-center">
                <Button
                    variant="outline"
                    className="rounded-r-none"
                    icon={
                        <Icon
                            icon="code"
                            className={classNames(showTriggerEditor && 'text-[#2d72d2]')}
                        />
                    }
                    text="Logic"
                    aria-pressed={showTriggerEditor}
                    onClick={() => setShowTriggerEditor(!showTriggerEditor)}
                />
                <Button
                    variant="outline"
                    className="-ml-px rounded-l-none"
                    icon={
                        <LightningIcon
                            className={classNames(showActionsPane && 'text-[#2d72d2]')}
                        />
                    }
                    text="Workflows"
                    aria-pressed={showActionsPane}
                    onClick={() => setShowActionsPane(!showActionsPane)}
                />
            </div>
            <Button
                minimal
                outlined
                className="rounded-lg"
                disabled={!validTrigger || !scheduleValid}
                title={
                    !validTrigger
                        ? 'Invalid Trigger'
                        : !scheduleValid
                          ? 'Select at least one day'
                          : undefined
                }
                intent="primary"
                text="Save"
                loading={saving}
                onClick={() => save()}
            />
        </div>
    ) : null

    const isSegmentsPage = location.pathname.startsWith('/segments')
    const breadcrumbs: BreadcrumbProps[] = isSegmentsPage
        ? [{ text: 'Automate' }, { href: '/segments', text: 'Segments' }]
        : [{ text: 'Automate' }, { href: '/recipes', text: 'Recipes' }]

    return (
        <Layout
            primary={primaryTitle}
            centerActions={centerActions}
            rightActions={rightActions}
            breadcrumbs={!asPublicRecipe ? breadcrumbs : undefined}
            className=""
            layoutClassName="!pt-0 !px-0 !pb-0 !overflow-visible"
        >
            <div className="flex w-full" style={{ height: `${totalHeight}px` }}>
                {/* Main content area - TriggerEditor + PreviewTable */}
                <div
                    className="flex flex-col"
                    style={{ width: showActionsPane ? `${editorWidth}%` : '100%' }}
                >
                    {/* Upper section - TriggerEditor */}
                    {showTriggerEditor && (
                        <div
                            className="flex flex-col"
                            style={{ height: `${upperSectionHeight}px` }}
                        >
                            <div className="flex-grow flex flex-col">
                                <div className="px-4 py-3 text-sm">Segment Logic</div>
                                <div className="px-4">
                                    <TriggerEditor
                                        value={recipeStore.recipe.trigger}
                                        onChange={(v: string) => {
                                            debouncedValidate(v, recipeStore.recipe)
                                            recipeStore.setTrigger(v)
                                        }}
                                        version={recipeStore.version}
                                        onPreviewRefresh={() => previewReloadRef.current?.()}
                                        height={upperSectionHeight - errorCalloutHeight}
                                        customFieldContext={customFieldEditorContext}
                                    />
                                    {customFieldCatalogError && (
                                        <Callout className="mt-2" intent="danger">
                                            Custom fields could not be loaded:{' '}
                                            {customFieldCatalogError}
                                        </Callout>
                                    )}
                                    {customFieldCatalogLoading && (
                                        <div className="mt-2 text-xs text-gray-500">
                                            Loading custom fields…
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Validation bar — outside the flex-grow div so it sits flush below the editor */}
                            <div className="px-4 pb-6" ref={errorCalloutRef}>
                                {!!error && (
                                    <div
                                        className="flex items-start gap-2 px-3 py-2 w-full"
                                        style={{
                                            background: '#22212B',
                                            borderTop: '1px solid #776EA3',
                                            borderRadius: '0 0 6px 6px',
                                        }}
                                    >
                                        <Icon
                                            icon="warning-sign"
                                            color="#FF9533"
                                            size={14}
                                            className="mt-0.5 shrink-0"
                                        />
                                        <span className="text-[#F8A45C] text-[13px]">
                                            <ErrorWindow error={error} value={errorValue ?? ''} />
                                        </span>
                                    </div>
                                )}
                                {!error && (
                                    <div
                                        className="flex items-center gap-2 px-3 py-2 w-full"
                                        style={{
                                            background: '#22212B',
                                            borderTop: '1px solid #776EA3',
                                            borderRadius: '0 0 6px 6px',
                                        }}
                                    >
                                        <Icon
                                            icon="tick-circle"
                                            color="#28CCB0"
                                            size={14}
                                            className="shrink-0"
                                        />
                                        <span className="text-[#776EA3] text-[13px]">
                                            Logic is valid
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Horizontal resize handle - only show when TriggerEditor is visible */}
                    {showTriggerEditor && (
                        <div
                            className={`relative h-2 cursor-row-resize bg-transparent transition-colors before:absolute before:inset-x-0 before:top-1/2 before:h-px before:-translate-y-1/2 before:bg-gray-200 hover:bg-gray-50 ${
                                isResizing ? 'bg-blue-50 before:bg-blue-300' : ''
                            }`}
                            onMouseDown={(e) => {
                                setIsResizing(true)
                                const startY = e.clientY
                                const startHeight = upperSectionHeight

                                const handleMouseMove = (e: MouseEvent) => {
                                    const deltaY = e.clientY - startY
                                    const newHeight = Math.max(
                                        250,
                                        Math.min(totalHeight - 300, startHeight + deltaY),
                                    )
                                    setUpperSectionHeight(newHeight)
                                }

                                const handleMouseUp = (e: MouseEvent) => {
                                    setIsResizing(false)
                                    const deltaY = e.clientY - startY
                                    const newHeight = Math.max(
                                        250,
                                        Math.min(totalHeight - 300, startHeight + deltaY),
                                    )
                                    try {
                                        localStorage.setItem(LS_UPPER_HEIGHT, String(newHeight))
                                    } catch {}
                                    document.removeEventListener('mousemove', handleMouseMove)
                                    document.removeEventListener('mouseup', handleMouseUp)
                                }

                                document.addEventListener('mousemove', handleMouseMove)
                                document.addEventListener('mouseup', handleMouseUp)
                            }}
                        />
                    )}

                    {/* Lower section - PreviewTable */}
                    <div
                        style={{
                            height: `${
                                showTriggerEditor
                                    ? totalHeight - upperSectionHeight - 8
                                    : totalHeight
                            }px`,
                        }}
                    >
                        <PreviewTable
                            recipe={recipeStore.recipe}
                            validTrigger={validTrigger}
                            onReloadRef={previewReloadRef}
                            backend={previewBackend}
                            fixedHeight={
                                showTriggerEditor
                                    ? totalHeight - upperSectionHeight - 8
                                    : totalHeight
                            }
                        />
                    </div>
                </div>

                {/* Vertical resize handle - only show when actions pane is visible */}
                {showActionsPane && (
                    <div
                        className={`relative w-2 cursor-col-resize bg-transparent transition-colors flex-shrink-0 before:absolute before:inset-y-0 before:left-1/2 before:w-px before:-translate-x-1/2 before:bg-gray-200 hover:bg-gray-50 ${
                            isResizingWidth ? 'bg-blue-50 before:bg-blue-300' : ''
                        }`}
                        style={{ height: `${totalHeight}px` }}
                        onMouseDown={(e) => {
                            setIsResizingWidth(true)
                            const startX = e.clientX
                            const startWidth = editorWidth
                            const containerRect =
                                e.currentTarget.parentElement?.getBoundingClientRect()

                            const handleMouseMove = (e: MouseEvent) => {
                                if (containerRect) {
                                    const deltaX = e.clientX - startX
                                    const containerWidth = containerRect.width
                                    const deltaPercent = (deltaX / containerWidth) * 100
                                    const newWidth = Math.max(
                                        30,
                                        Math.min(80, startWidth + deltaPercent),
                                    )
                                    setEditorWidth(newWidth)
                                }
                            }

                            const handleMouseUp = (e: MouseEvent) => {
                                setIsResizingWidth(false)
                                if (containerRect) {
                                    const deltaX = e.clientX - startX
                                    const containerWidth = containerRect.width
                                    const deltaPercent = (deltaX / containerWidth) * 100
                                    const newWidth = Math.max(
                                        30,
                                        Math.min(80, startWidth + deltaPercent),
                                    )
                                    try {
                                        localStorage.setItem(LS_EDITOR_WIDTH, String(newWidth))
                                    } catch {}
                                }
                                document.removeEventListener('mousemove', handleMouseMove)
                                document.removeEventListener('mouseup', handleMouseUp)
                            }

                            document.addEventListener('mousemove', handleMouseMove)
                            document.addEventListener('mouseup', handleMouseUp)
                        }}
                    />
                )}

                {/* Right sidebar - Workflow panel - Full height */}
                {showActionsPane && (
                    <fieldset
                        disabled={saving}
                        aria-busy={saving}
                        className="m-0 min-h-0 min-w-0 overflow-y-auto border-0 bg-white p-0"
                        style={{ width: `${100 - editorWidth}%`, height: `${totalHeight}px` }}
                    >
                        <WorkflowPanel clockSchedulingAvailable={isPrivateV2Segment} />
                    </fieldset>
                )}
            </div>
            {isPrivateV2Segment && detailScheduleProfileId && detailScheduleRecipeId && (
                <div className="px-4 py-4">
                    <ScheduledRunsTable
                        profileId={detailScheduleProfileId}
                        recipeId={detailScheduleRecipeId}
                        title="Scheduled runs"
                    />
                </div>
            )}
        </Layout>
    )
})

const AfterEverDataSyncContent = () => {
    return <div className="text-xs pt-1 text-gray-500">4x per day</div>
}

// on M,W,T,F
// date select, repeats (never | weekly | biweekly | monthly | quarterly | yearly)

type FrequencyParamsAction =
    | {
          type: 'TOGGLE_DAY'
          payload: Day
      }
    | {
          type: 'SET_DATE'
          payload: Date
      }
    | {
          type: 'SET_REPEAT'
          payload: Repeat
      }
    | {
          type: 'SET_FREQUENCY'
          payload: Frequency
      }
    | {
          type: 'SET'
          payload: FrequencyParams
      }

const frequencyParamsReducer = (
    state: FrequencyParams,
    action: FrequencyParamsAction,
): FrequencyParams => {
    switch (action.type) {
        case 'TOGGLE_DAY':
            const exists = state?.days?.indexOf(action.payload) !== -1
            if (exists) {
                return {
                    ...state,
                    days: state?.days?.filter((d) => d !== action.payload),
                }
            } else {
                return {
                    ...state,
                    days: state.days ? [...state.days, action.payload] : [action.payload],
                }
            }
        case 'SET_FREQUENCY':
            return {
                ...state,
                frequency: action.payload,
            }
        case 'SET_DATE':
            return {
                ...state,
                date: action.payload,
            }
        case 'SET_REPEAT':
            return {
                ...state,
                repeat: action.payload,
            }
        case 'SET':
            return action.payload
        default:
            return state
    }
}

const OnCertainDaysContent = ({
    frequency,
    frequency_params,
    onChange,
}: OnCertainDatesContentProps) => {
    const [showModal, setShowModal] = useState(false)
    const [frequencyParamsDirty, dispatch] = useReducer(frequencyParamsReducer, frequency_params)

    const isValid = useMemo(() => {
        if (frequencyParamsDirty?.days) {
            return frequencyParamsDirty.days.length > 0
        }

        return true
    }, [frequencyParamsDirty])

    useEffect(() => {
        dispatch({
            type: 'SET',
            payload: frequency_params,
        })
    }, [frequency_params])

    const days: Day[] = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
    ]

    const frequencyDescription = getFrequencyDescription(frequency_params)

    const handleFrequencyChange = (frequency: Frequency) => {
        dispatch({
            type: 'SET_FREQUENCY',
            payload: frequency,
        })
    }

    return (
        <div>
            <div
                title="Click to edit"
                className="mb-3 text-blue-500 hover:underline underline-offset-1 cursor-pointer"
                onClick={() => setShowModal(true)}
            >
                {frequencyDescription}
            </div>

            <Dialog
                className="p-4"
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                canEscapeKeyClose
                canOutsideClickClose
            >
                <label className="mb-0">Frequency</label>
                <FrequencySelectRadios
                    params={frequencyParamsDirty}
                    onChange={handleFrequencyChange}
                />

                <label className="mb-0">Days</label>
                {days.map((day) => (
                    <Checkbox
                        className="mb-0"
                        label={capitalCase(day)}
                        value={day}
                        key={day}
                        checked={frequencyParamsDirty?.days?.indexOf(day) !== -1}
                        onChange={() => {
                            dispatch({
                                type: 'TOGGLE_DAY',
                                payload: day,
                            })
                        }}
                    />
                ))}

                {!isValid && (
                    <Callout className="mt-3" intent="danger">
                        You must select at least one day
                    </Callout>
                )}

                <div className="mt-3 flex justify-between">
                    <Button
                        text="Cancel"
                        onClick={() => {
                            dispatch({
                                type: 'SET',
                                payload: frequency_params,
                            })
                            setShowModal(false)
                        }}
                    />
                    <Button
                        minimal
                        outlined
                        intent="primary"
                        text="Save"
                        disabled={!isValid}
                        onClick={() => {
                            onChange(frequency, frequencyParamsDirty)
                            setShowModal(false)
                        }}
                    />
                </div>
            </Dialog>
        </div>
    )
}

interface OnCertainDatesContentProps {
    frequency: Frequency
    frequency_params: FrequencyParams
    onChange: (frequency: Frequency, frequency_params: FrequencyParams) => any
}
const OnCertainDatesContent = ({
    frequency,
    frequency_params,
    onChange,
}: OnCertainDatesContentProps) => {
    const [showModal, setShowModal] = useState(false)
    const [frequencyParamsDirty, dispatch] = useReducer(frequencyParamsReducer, frequency_params)

    useEffect(() => {
        dispatch({
            type: 'SET',
            payload: frequency_params,
        })
    }, [frequency_params])

    const renderer: ItemRenderer<string> = (key, params) => {
        return (
            <DateScheduleBox
                className="hover:bg-gray-50"
                key={key}
                dateSchedule={key}
                date={frequencyParamsDirty?.date ?? new Date()}
                // @ts-expect-error
                onClick={params.handleClick}
            />
        )
    }

    const frequencyDescription = getFrequencyDescription(frequency_params)

    const handleFrequencyChange = (frequency: Frequency) => {
        dispatch({
            type: 'SET_FREQUENCY',
            payload: frequency,
        })
    }

    const repeatList: Repeat[] = ['never', 'weekly', 'biweekly', 'monthly', 'quarterly', 'yearly']

    return (
        <div>
            <div
                title="Click to edit"
                className="mb-3 text-blue-500 hover:underline underline-offset-1 cursor-pointer"
                onClick={() => setShowModal(true)}
            >
                {frequencyDescription}
            </div>

            <Dialog
                className="p-4"
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                canEscapeKeyClose
                canOutsideClickClose
            >
                <label className="mb-0">Frequency</label>
                <FrequencySelectRadios
                    params={frequencyParamsDirty}
                    onChange={handleFrequencyChange}
                />

                <label className="mb-0">Date</label>
                <div>
                    <DateInput
                        value={frequencyParamsDirty?.date}
                        formatDate={(d) => format(d, 'Y-M-d')} // @todo format to something prettier
                        onChange={(date) => {
                            dispatch({
                                type: 'SET_DATE',
                                payload: date,
                            })
                        }}
                        // parseDate={} // @todo add parseDate
                    />
                </div>

                <label className="mb-0 mt-3">Repeats</label>
                <div>
                    <Select
                        items={repeatList}
                        itemRenderer={renderer}
                        onItemSelect={(e) => {
                            dispatch({
                                type: 'SET_REPEAT',
                                payload: e as Repeat,
                            })
                        }}
                        filterable={false}
                    >
                        <div className="bg-white bp4-input h-auto">
                            <DateScheduleBox
                                dateSchedule={frequencyParamsDirty?.repeat ?? 'daily'}
                                date={frequencyParamsDirty?.date ?? new Date()}
                                className=""
                                onClick={() => {}}
                            />
                        </div>
                    </Select>
                </div>

                <div className="mt-3 flex justify-between">
                    <Button
                        text="Cancel"
                        onClick={() => {
                            dispatch({
                                type: 'SET',
                                payload: frequency_params,
                            })
                            setShowModal(false)
                        }}
                    />
                    <Button
                        minimal
                        outlined
                        intent="primary"
                        text="Save"
                        onClick={() => {
                            onChange(frequency, frequencyParamsDirty)
                            setShowModal(false)
                        }}
                    />
                </div>
            </Dialog>
        </div>
    )
}

const frequencies: SelectBoxOption[] = [
    {
        label: 'After every data sync',
        value: FrequenciesList.AfterEveryDataSync,
        content: AfterEverDataSyncContent,
    },
    { label: 'Once per Day', value: FrequenciesList.OncePerDay },
    { label: 'Manually', value: FrequenciesList.Manually },
    {
        label: 'On Certain Days',
        value: FrequenciesList.OnCertainDays,
        content: OnCertainDaysContent,
    },
    {
        label: 'On Certain Dates',
        value: FrequenciesList.OnCertainDates,
        content: OnCertainDatesContent,
    },
    {
        label: 'At a Certain Time',
        value: FrequenciesList.AtACertainTime,
        disabled: true,
        tooltip: 'Coming soon!',
    },
]

interface FrequencyBadgeProps {
    frequency: Frequency
    frequency_params: FrequencyParams
}
export const FrequencyBadge = ({ frequency, frequency_params }: FrequencyBadgeProps) => {
    if (frequency === FrequenciesList.Scheduled) return <span>Scheduled</span>

    const f = frequencies.find((f) => f.value === frequency)
    if (f) {
        switch (f.value) {
            case FrequenciesList.OnCertainDays:
            case FrequenciesList.OnCertainDates:
                return <span>{getFrequencyDescription(frequency_params)}</span>
            default:
                return <span>{f.label}</span>
        }
    }

    console.error('Cannot resolve frequency badge for', frequency, frequency_params)
    return <span />
}
